import autobind from 'autobind-decorator';
import { markRaw } from '#imports';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import armsRum from '@arms/rum-browser';
import IMSdk from '@hetao/imsdk-new';
import { RouterService } from '@core/services/router.service';
import { StorageService } from '@core/services/storage.service';
import type { MsgVO } from '@core/types/im';
import { isProd } from '@core/utils/environment';
import {
  IM_CONFIG_APPID_DEV,
  IM_CONFIG_APPID_PROD,
  IM_CONFIG_APPKEY_DEV,
  IM_CONFIG_APPKEY_PROD,
} from './constant';
import { ImService } from './im.service';

@Injectable()
export class SdkService {
  // 融云sdk实例
  public sdkInstance = markRaw<any>({});

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => ImService))
  public imService!: ImService;

  public init() {
    const sdkConf = this.getConfig();
    const instance = new IMSdk(sdkConf, (event: any) => {
      console.log('埋点回调', event);
    });

    // 监听融云连接状态切换
    instance.on('connectStatusChange', this.handleConnectStatusChange);

    // 监听融云消息
    instance.on('imMsg', this.handleMessage);

    this.sdkInstance = markRaw(instance);
  }

  public destroy() {
    if (this.sdkInstance && this.sdkInstance.destroy) {
      this.sdkInstance.destroy();
    }
  }

  private getConfig() {
    return {
      appEnv: isProd() ? 'prod' : 'testing', // 融云初始化环境
      userId: this.imService.self.id, // 当前的用户 id
      type: this.imService.selfType, // 当前的 type，获取 token 的前缀 目前有 student 和 teacher
      appIds: {
        // 测试环境和正式环境的 appId，编程：uniq_channel  扩科：live-service
        dev: IM_CONFIG_APPID_DEV,
        prod: IM_CONFIG_APPID_PROD,
      },
      appKeys: {
        // 测试环境和正式环境的 appKey @李廷忠 @散宝峰 @刘关强
        dev: IM_CONFIG_APPKEY_DEV,
        prod: IM_CONFIG_APPKEY_PROD,
      },
    };
  }

  // 处理融云连接状态切换
  @autobind
  private handleConnectStatusChange(status: number) {
    // 上报所有连接状态变化，用于关联"断连时段"和"消息丢失时段"
    armsRum.sendCustom({
      type: 'custom',
      name: `oj_im_connect_status_${status}`,
      value: 1,
    });
    if (status === 0) {
      console.log('[IM] 融云连接成功，开始加入聊天室');
      const roomId = `STATUS_ROOM_${this.imService.peerId}`;
      this.sdkInstance.joinChatRoom(roomId);
    } else if (status === 1) {
      console.log('[IM] 融云连接中…');
    } else if (status === 3) {
      console.warn('[IM] 融云连接断开', new Date().toISOString());
    }
    this.imService.setSelfOnline(status === 0);
  }

  @autobind
  private handleMessage(data: MsgVO) {
    let parsed: ReturnType<typeof this.parseMessage>;
    try {
      parsed = this.parseMessage(data);
    } catch (err) {
      // [埋点 B] parseMessage 内部抛出的未预期异常兜底捕获
      console.error('[IM] parseMessage 解析异常', err, data);
      armsRum.sendCustom({
        type: 'custom',
        name: 'oj_im_parse_error',
        value: 1,
      });
      return;
    }

    if (!parsed) {
      // parseMessage 内部已处理离线消息日志，此处直接返回
      return;
    }

    const teacherId = this.imService.peerId;
    // [console C] 每条消息记录 sender 和 peerId，用于格式比对
    console.log(
      '[IM] 收到消息 sender:',
      parsed.sender,
      '| 期望 teacherId:',
      teacherId,
      '| 匹配:',
      parsed.sender === teacherId,
    );

    if (parsed.sender === teacherId || parsed.sender === 'system') {
      // [埋点 D] 消息成功通过过滤，进入业务处理（用于分母统计）
      armsRum.sendCustom({
        type: 'custom',
        name: 'oj_im_msg_received',
        value: 1,
      });
      this.imService.messageService.handleReceiveMessage(parsed);
    } else {
      // [埋点 C] sender 不匹配，关键诊断埋点
      console.warn(
        '[IM] sender 不匹配，消息被过滤 | sender:',
        parsed.sender,
        '| teacherId:',
        teacherId,
      );
      armsRum.sendCustom({
        type: 'custom',
        name: 'oj_im_sender_mismatch',
        value: 1,
      });
    }
  }

  // 收到的融云消息经过 base64，需要解密处理
  private parseMessage(originMessage: any) {
    // [埋点 A] 离线消息丢弃：网络断连重连时融云会重推离线消息，当前逻辑直接丢弃
    if (originMessage.offLineMessage || originMessage.isOffLineMessage) {
      console.warn('[IM] 离线消息丢弃，频繁出现则说明存在网络断连', {
        offLineMessage: originMessage.offLineMessage,
        isOffLineMessage: originMessage.isOffLineMessage,
      });
      armsRum.sendCustom({
        type: 'custom',
        name: 'oj_im_offline_msg_dropped',
        value: 1,
      });
      return;
    }
    // 以下 JSON.parse 若抛出异常，由 handleMessage 的 try/catch 统一捕获
    const msg = JSON.parse(originMessage.content.content);
    if (msg && msg.msgBody) {
      const data = {
        sender: this.decodeBase64(msg.sender),
        receivers: msg.receiverInfo.private.users.map((item: string) =>
          this.decodeBase64(item),
        ),
        msgBody: JSON.parse(this.decodeBase64(msg.msgBody)),
        msgId: msg.msgId,
        msgTime: msg.msgTime || originMessage.sentTime / 1000,
        readStatus: 0,
        offLineMessage: msg.offLineMessage || originMessage.isOffLineMessage,
      };
      return data;
    } else if (msg && msg.statusInfo) {
      this.imService.updateTeacherStatus(msg);
    }
  }

  private decodeBase64(base64: string) {
    return decodeURIComponent(escape(window.atob(base64)));
  }
}
